import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// How long to keep re-aiming at a hash target while the page above it finishes loading
const SETTLE_TIME_MS = 1500;

// Start each new page at the top instead of keeping the previous scroll position.
// Links with a hash (e.g. /#contact) scroll to the element with that id instead.
export default function ScrollToTop() {
	const { hash, key } = useLocation();

	// Runs on every navigation (the key changes each time), so clicking the same
	// link again, like Contact while already on the home page, still scrolls
	useEffect(() => {
		if (!hash) {
			window.scrollTo(0, 0);
			return;
		}

		const scrollToTarget = () => {
			document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
		};

		scrollToTarget();

		// Images and fonts above the target can still be loading, which pushes it further
		// down after we've scrolled. Re-aim whenever the page height changes, until it settles.
		const observer = new ResizeObserver(scrollToTarget);
		observer.observe(document.body);

		// Stop re-aiming once the page settles, or as soon as the visitor scrolls on their own
		const stop = () => observer.disconnect();
		const timeout = window.setTimeout(stop, SETTLE_TIME_MS);
		window.addEventListener("wheel", stop, { once: true, passive: true });
		window.addEventListener("touchstart", stop, { once: true, passive: true });

		return () => {
			stop();
			window.clearTimeout(timeout);
			window.removeEventListener("wheel", stop);
			window.removeEventListener("touchstart", stop);
		};
	}, [key, hash]);

	return null;
}
